// @ts-nocheck
import Fetch from '@/utils/Fetch';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import permissionService from '@/services/permission/permission-service';
import constants from '@/utils/constants';
import { claimToStringArray, decodeJwtPayload } from '@/utils/jwt';

const getEmployees = async (multiStoreID: any, includeDisabled = false) => {

    const subInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    const multiStore = (subInfo && subInfo.MultiStore) === true;

    const employeeResult = await Fetch.get({
        url: "/Employee/GetEmployees", params: {
            storeID: multiStore ? multiStoreID : null,
            includeDisabled: includeDisabled
        },
    });

    return employeeResult;
};

const getEmployee = async (id: any) => {
    return await Fetch.get({
        url: `/Employee?id=${id}`,
    });
};

const getColumnMappings = async (model: any) => {
    return await Fetch.get({
        url: '/Employee/ColumnMapping',
        params: {
            model
        },
    });
};

const saveColumnMappings = async (mappings: any, model: any, toast = null) => {
    await Fetch.put({
        url: '/Employee/ColumnMapping',
        params: {
            ColumnMappings: mappings,
            model,
        },
        toastCtx: toast,
    });
};

/**
 * Decode the claims of the current PlatformAuth access token.
 * Returns `null` when the token is missing, not a decodable JWT, or not a PlatformAuth
 * token. Legacy ServCraft tokens ARE decodable JWTs but carry no `iss` claim (the same
 * discriminator the backend TokenAuthHandler uses) and no permissions claim — deriving
 * permissions from one would wipe the stored set for every pre-cutover session. `null`
 * means "could not read the token" — callers must NOT treat it as an empty permission set.
 */
const getPlatformJwtClaims = (): any | null => {
    if (typeof window === "undefined") return null;

    const token = Storage.getCookie('platformAccessToken') || Storage.getCookie(Enums.Cookie.token);
    const claims = decodeJwtPayload(token);
    return claims?.iss ? claims : null;
};

const getPermissionsFromClaims = (claims: any): string[] =>
    claimToStringArray(claims['https://servcraft.com/permissions']);

const refreshPermissions = async () => {
    if (typeof window === "undefined") return;

    const lastRefreshed = window.localStorage.getItem(Enums.LocalStorage.LastRefreshedPermissions);
    const newRefreshed = new Date().valueOf();

    if (lastRefreshed && newRefreshed - lastRefreshed < constants.permissionsRefreshIntervalMS) return;

    // Permissions and app_roles live in the PlatformAuth JWT. PlatformAuth re-derives
    // both from the tenant DB every time the token rotates (the 2-minute proactive check
    // in the auth layout, or the 401 refresh-retry in Fetch.ts), and a permission edit
    // normally revokes the session outright, forcing a re-login. This re-decode is the
    // fallback for changes that escape revocation — the revoke-user notify is best-effort
    // (CacheBL.NotifyUserRevocationAsync never throws), and Owner transfers
    // (CedeOwnershipAsync) don't revoke at all — so once a rotated token carries
    // different claims, the DTK-119 route re-check in _authenticated.tsx kicks the user
    // off pages they can no longer access. No network round-trip happens here.
    const claims = getPlatformJwtClaims();
    if (!claims) {
        // Unreadable token ≠ no permissions. Wiping the stored set here would strand a
        // logged-in user with zero permissions (and blank navigation) on any decode
        // hiccup — keep the existing set and let the 401 path decide the session's fate.
        //
        // Self-heal: a legacy session with an EMPTY stored set is almost certainly one
        // whose permissions were wiped by the pre-fix claim re-derivation. Restore them
        // via the legacy endpoint (the pre-overhaul refresh source).
        // TODO: remove this self-heal block (and the iss gate in getPlatformJwtClaims)
        // once legacy auth is fully retired — every session will then carry a
        // PlatformAuth JWT and the legacy /Employee/UserPermissions path is dead.
        const stored = permissionService.getPermissions();
        if (!stored || stored.length === 0) {
            const permissionIDs: any = await Fetch.get({ url: '/Employee/UserPermissions' });
            if (Array.isArray(permissionIDs) && permissionIDs.length > 0) {
                permissionService.setPermissions(permissionIDs, permissionService.hasPermission(Enums.PermissionName.Owner), 7);
                window.localStorage.setItem(Enums.LocalStorage.LastRefreshedPermissions, (newRefreshed as any));
                return true;
            }
        }
        return false;
    }

    const oldPermissions = JSON.stringify(permissionService.getPermissions());

    const jwtPermissions = getPermissionsFromClaims(claims);
    // Owner must come from the token's app_roles (re-derived from CustomerUser.IsOwner on
    // every rotation), NOT from the previously stored set — reading it back from storage
    // made the flag self-perpetuating, so an Owner demotion never reached the UI.
    const appRoles = claimToStringArray(claims.app_roles);
    const isOwner = appRoles.includes('Owner');
    permissionService.setPermissions(jwtPermissions, isOwner, 7);
    window.localStorage.setItem(Enums.LocalStorage.LastRefreshedPermissions, (newRefreshed as any));

    // DTK-119: report whether the effective permission set changed so callers
    // (e.g. src/routes/_authenticated.tsx) can re-render and re-check route access
    // promptly instead of waiting for a full reload.
    const newPermissions = JSON.stringify(permissionService.getPermissions());
    return oldPermissions !== newPermissions;
}

const getPrimaryRoles = async () => {
    const primaryRoles = await Fetch.get({
        url: '/Employee/PrimaryRole'
    });

    return primaryRoles.Results;
}

/**
 * Ask the server whether an email address or mobile number is still free, so the inline
 * field check can warn before the save is attempted. Never throws and never rejects: a
 * network or server failure resolves as "both available", because a backend problem must
 * not stop somebody filling the form in. The save response stays authoritative.
 */
const checkAvailability = async ({ EmailAddress = null, MobileNumber = null, EmployeeID = null }: any = {}) => {
    const unknown = { EmailAvailable: true, MobileAvailable: true, EmailMessage: null, MobileMessage: null };

    try {
        const response = await Fetch.post({
            url: '/Employee/CheckAvailability',
            params: {
                EmailAddress,
                MobileNumber,
                EmployeeID
            }
        });

        if (!response || typeof response !== 'object') return unknown;

        return {
            // Anything other than an explicit false is treated as available, so a partial or
            // unexpected payload (a 401 envelope, say) reads as "no problem found".
            EmailAvailable: response.EmailAvailable !== false,
            MobileAvailable: response.MobileAvailable !== false,
            EmailMessage: response.EmailMessage ?? null,
            MobileMessage: response.MobileMessage ?? null
        };
    } catch {
        return unknown;
    }
};

export default {
    getEmployees,
    getEmployee,
    getColumnMappings,
    saveColumnMappings,
    refreshPermissions,
    getPrimaryRoles,
    checkAvailability
};